/**
 * PumpSwap AMM Integration
 *
 * PumpSwap is pump.fun's native AMM for graduated tokens.
 * When a token graduates from the bonding curve, liquidity migrates to PumpSwap.
 */

import {
  Connection,
  PublicKey,
  TransactionInstruction,
  SystemProgram,
} from '@solana/web3.js';
import {
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddress,
} from '@solana/spl-token';

// PumpSwap Program ID
export const PUMPSWAP_PROGRAM_ID = new PublicKey('pAMMBay6oceH9fJKBRHGP5D4bD4sWpmSwMn52FMfXEA');

// WSOL mint
const WSOL_MINT = new PublicKey('So11111111111111111111111111111111111111112');

/**
 * PumpSwap pool state
 */
export interface PumpSwapPoolState {
  tokenMint: PublicKey;
  tokenReserve: bigint;
  solReserve: bigint;
  lpSupply: bigint;
  feeNumerator: number;
  feeDenominator: number;
}

/**
 * Derive the pool PDA for a PumpSwap token
 */
export function getPoolPDA(mint: PublicKey): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from('pool'), mint.toBuffer(), WSOL_MINT.toBuffer()],
    PUMPSWAP_PROGRAM_ID
  );
  return pda;
}

/**
 * Derive the pool token vault
 */
export function getPoolTokenVault(pool: PublicKey, mint: PublicKey): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from('token_vault'), pool.toBuffer(), mint.toBuffer()],
    PUMPSWAP_PROGRAM_ID
  );
  return pda;
}

/**
 * Derive the pool SOL vault
 */
export function getPoolSolVault(pool: PublicKey): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from('sol_vault'), pool.toBuffer()],
    PUMPSWAP_PROGRAM_ID
  );
  return pda;
}

/**
 * Check if token has a PumpSwap pool (graduated from pump.fun)
 */
export async function hasPumpSwapPool(
  connection: Connection,
  mint: PublicKey
): Promise<boolean> {
  const pool = getPoolPDA(mint);
  const accountInfo = await connection.getAccountInfo(pool);
  return accountInfo !== null;
}

/**
 * Fetch pool state from chain
 */
export async function getPoolState(
  connection: Connection,
  mint: PublicKey
): Promise<PumpSwapPoolState | null> {
  const pool = getPoolPDA(mint);
  const accountInfo = await connection.getAccountInfo(pool);

  if (!accountInfo) {
    return null;
  }

  const data = accountInfo.data;

  if (data.length < 120) {
    return null;
  }

  try {
    // Parse pool data - this is an approximation of the layout
    return {
      tokenMint: new PublicKey(data.slice(8, 40)),
      tokenReserve: data.readBigUInt64LE(72),
      solReserve: data.readBigUInt64LE(80),
      lpSupply: data.readBigUInt64LE(88),
      feeNumerator: data.readUInt16LE(96),
      feeDenominator: data.readUInt16LE(98),
    };
  } catch {
    return null;
  }
}

/**
 * Calculate buy output using constant product formula
 */
export function calculateBuyOutput(
  solAmount: bigint,
  state: PumpSwapPoolState
): bigint {
  const fee = (solAmount * BigInt(state.feeNumerator)) / BigInt(state.feeDenominator);
  const solAmountAfterFee = solAmount - fee;

  // x * y = k formula
  const numerator = solAmountAfterFee * state.tokenReserve;
  const denominator = state.solReserve + solAmountAfterFee;

  return numerator / denominator;
}

/**
 * Calculate sell output
 */
export function calculateSellOutput(
  tokenAmount: bigint,
  state: PumpSwapPoolState
): bigint {
  const numerator = tokenAmount * state.solReserve;
  const denominator = state.tokenReserve + tokenAmount;
  const solOut = numerator / denominator;

  // Apply fee
  const fee = (solOut * BigInt(state.feeNumerator)) / BigInt(state.feeDenominator);
  return solOut - fee;
}

/**
 * Apply slippage
 */
export function applySlippage(amount: bigint, slippageBps: number): bigint {
  return (amount * BigInt(10000 - slippageBps)) / BigInt(10000);
}

/**
 * Build buy instruction
 */
export async function buildBuyInstruction(params: {
  mint: PublicKey;
  userWallet: PublicKey;
  solAmount: bigint;
  minTokensOut: bigint;
}): Promise<TransactionInstruction> {
  const { mint, userWallet, solAmount, minTokensOut } = params;

  const pool = getPoolPDA(mint);
  const tokenVault = getPoolTokenVault(pool, mint);
  const solVault = getPoolSolVault(pool);
  const userTokenAccount = await getAssociatedTokenAddress(mint, userWallet);

  // Buy instruction discriminator (approximate)
  const BUY_DISCRIMINATOR = Buffer.from([0x66, 0x06, 0x3d, 0x12, 0x01, 0xda, 0xeb, 0xea]);

  const data = Buffer.alloc(24);
  BUY_DISCRIMINATOR.copy(data, 0);
  data.writeBigUInt64LE(solAmount, 8);
  data.writeBigUInt64LE(minTokensOut, 16);

  return new TransactionInstruction({
    programId: PUMPSWAP_PROGRAM_ID,
    keys: [
      { pubkey: pool, isSigner: false, isWritable: true },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: tokenVault, isSigner: false, isWritable: true },
      { pubkey: solVault, isSigner: false, isWritable: true },
      { pubkey: userTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userWallet, isSigner: true, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data,
  });
}

/**
 * Build sell instruction
 */
export async function buildSellInstruction(params: {
  mint: PublicKey;
  userWallet: PublicKey;
  tokenAmount: bigint;
  minSolOut: bigint;
}): Promise<TransactionInstruction> {
  const { mint, userWallet, tokenAmount, minSolOut } = params;

  const pool = getPoolPDA(mint);
  const tokenVault = getPoolTokenVault(pool, mint);
  const solVault = getPoolSolVault(pool);
  const userTokenAccount = await getAssociatedTokenAddress(mint, userWallet);

  // Sell instruction discriminator (approximate)
  const SELL_DISCRIMINATOR = Buffer.from([0x33, 0xe6, 0x85, 0xa4, 0x01, 0x7f, 0x83, 0xad]);

  const data = Buffer.alloc(24);
  SELL_DISCRIMINATOR.copy(data, 0);
  data.writeBigUInt64LE(tokenAmount, 8);
  data.writeBigUInt64LE(minSolOut, 16);

  return new TransactionInstruction({
    programId: PUMPSWAP_PROGRAM_ID,
    keys: [
      { pubkey: pool, isSigner: false, isWritable: true },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: tokenVault, isSigner: false, isWritable: true },
      { pubkey: solVault, isSigner: false, isWritable: true },
      { pubkey: userTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userWallet, isSigner: true, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data,
  });
}

/**
 * Get buy quote
 */
export async function getBuyQuote(
  connection: Connection,
  mint: PublicKey,
  solAmount: bigint,
  slippageBps: number
): Promise<{
  expectedTokens: bigint;
  minTokensOut: bigint;
  priceImpact: number;
}> {
  const state = await getPoolState(connection, mint);

  if (!state) {
    throw new Error('Token not found on PumpSwap');
  }

  const expectedTokens = calculateBuyOutput(solAmount, state);
  const minTokensOut = applySlippage(expectedTokens, slippageBps);

  // Calculate price impact
  const priceBefore = Number(state.solReserve) / Number(state.tokenReserve);
  const priceAfter = Number(state.solReserve + solAmount) /
                     Number(state.tokenReserve - expectedTokens);
  const priceImpact = ((priceAfter - priceBefore) / priceBefore) * 100;

  return {
    expectedTokens,
    minTokensOut,
    priceImpact,
  };
}

/**
 * Get sell quote
 */
export async function getSellQuote(
  connection: Connection,
  mint: PublicKey,
  tokenAmount: bigint,
  slippageBps: number
): Promise<{
  expectedSol: bigint;
  minSolOut: bigint;
  priceImpact: number;
}> {
  const state = await getPoolState(connection, mint);

  if (!state) {
    throw new Error('Token not found on PumpSwap');
  }

  const expectedSol = calculateSellOutput(tokenAmount, state);
  const minSolOut = applySlippage(expectedSol, slippageBps);

  const priceBefore = Number(state.solReserve) / Number(state.tokenReserve);
  const priceAfter = Number(state.solReserve - expectedSol) /
                     Number(state.tokenReserve + tokenAmount);
  const priceImpact = ((priceBefore - priceAfter) / priceBefore) * 100;

  return {
    expectedSol,
    minSolOut,
    priceImpact,
  };
}
